import {Button} from 'platform/components';
import {Center, Heading, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

export type NewVehicleSlotChoiceProps = {
  /** Filling the slot from stock. Omitted when the user may not add one. */
  stockAction?: {onClick: VoidFunction; isDisabled?: boolean};
  /** Building a new vehicle from the catalogue. Omitted without write permission. */
  onConfigure?: VoidFunction;
  /** Committing a configuration that already exists. Omitted without ordering permission. */
  onUseExisting?: VoidFunction;
};

/**
 * A Business Case sells one vehicle, and there are two ways to fill that slot. Presenting them as
 * two panels made the new-vehicle route look bolted on beside the real one, so both answers live
 * in a single block drawn in the same visual language as the platform empty state it replaces.
 */
export function NewVehicleSlotChoice(props: NewVehicleSlotChoiceProps) {
  return (
    <Center width="100%" data-testid="business-case-selling-no-vehicle">
      <VStack align="center">
        <Icon size={8} value="maps/directions_car" color="palettes.neutral.100.100" />
        <Space vertical={3} />
        <Heading size={5} color="tertiary">
          {i18n.t('page.businessCase.notifications.noVehicleAddedToSale')}
        </Heading>
        <Space vertical={1} />
        <Text size="xSmall" color="tertiary" align="center">
          {t(
            'entity.newVehicle.businessCase.addFromStockOrOrder',
            'Sell one from stock, or configure a new one to order'
          )}
        </Text>
        <Space vertical={4} />
        <HStack spacing={2}>
          <Show when={isNotNil(props.stockAction)}>
            <Button
              variant="secondary"
              leftIcon="content/add_circle"
              title={i18n.t('entity.vehicle.actions.addVehicleToSell')}
              isDisabled={props.stockAction?.isDisabled}
              onClick={() => props.stockAction?.onClick()}
            />
          </Show>
          <Show when={isNotNil(props.onConfigure)}>
            <Button
              variant="secondary"
              leftIcon="content/add_circle"
              title={t('entity.newVehicle.actions.configure', 'Configure new vehicle')}
              onClick={() => props.onConfigure?.()}
            />
          </Show>
        </HStack>
        <Show when={isNotNil(props.onUseExisting)}>
          <Space vertical={2} />
          <Button
            variant="link"
            size="small"
            title={t(
              'entity.newVehicle.actions.useExistingConfiguration',
              'Use existing configuration'
            )}
            onClick={() => props.onUseExisting?.()}
          />
        </Show>
      </VStack>
    </Center>
  );
}
